/**
 * THE POPUP — one shared element (owner 24.9.2026: "Ja add friends elementti voi olla yhteinen kuten on HOW TO PLAY!").
 * The 9-slice frame with its four corner palms, the title lane, the drawn rule under it and the close × in the corner:
 * the same drawing in every popup, so a popup is never built a second time. The × is the owner's own cut of the CREDITS
 * button, supplied 21.9.2026 — never a new one ("ET SAA KEKSIÄ JO TEHTYJÄ ELEMENTTEJÄ UUDELLEEN!!! OTA X BUTTON HOW TO
 * PLAY popupista!").
 *
 * A popup is NOT tinted to the room it opens over (owner 24.9.2026: "EI TARVITSE SÄVYTTÄÄ POPUPPEJA! … Ne on sävytetty
 * ja sävytetään pelin yleisilmeen mukaan. Kuten SETTINGS ja HOW TO PLAY!"). It carries the game's own look, whatever
 * table is behind it.
 *
 * The frame is a 9-slice (docs/UI_RULES.md §2b): corners that never stretch, edges that stretch in one direction, a
 * middle that stretches in both. The panel therefore has no fixed height — it grows with its content, and nothing here
 * measures anything (OD-036). Left without a place it centres itself on the stage with auto margins.
 *
 * Two plates: HOW TO PLAY keeps the frame the owner approved on 21.9., with its corner palms. ADD FRIENDS takes the
 * owner's own panel9 (24.9.2026, materials/panel9-tausta.png, published as delivered) — no palms, sliced at its corner
 * arcs and drawn at their natural proportion, art/src/popup-panel9-2026-09-24/README.md.
 */
import type { ComponentChildren } from 'preact';
import { art } from '../art';

/** where the artwork is sliced: the neon curve ends at 48 px and 10 px is left for its glow (owner 21.9.2026) */
const SLICE = 58;
/** the popup's box on the stage: 820 wide at x = 60 (Codex's mockup, art/reference/vedos-howto-popup-2026-09-21.md) */
export const POPUP = { x: 60, y: 206, w: 820 };

export function PopupPanel({ title, titleArt, label, onClose, closeTestid, testid, place, plate = 'howto', palms = true, rule = true, head = true, cls = '', children }: {
  title: string;
  /** a drawn title instead of the text one: the piece's whole canvas, placed by the popup's own CSS */
  titleArt?: string;
  /** the dialog's name for a screen reader; the title is used when it is left out */
  label?: string;
  onClose: () => void;
  closeTestid: string;
  testid?: string;
  /** an absolute place on the stage; left out, the popup centres itself and grows with its content */
  place?: { x: number; y: number; w?: number };
  /** 'howto' is the 21.9. frame; the rest are the owner's own backgrounds */
  plate?: 'howto' | 'panel9' | 'addfriends' | 'crew';
  /** the game's corner palms, on by default */
  palms?: boolean;
  /** the drawn line under a text title */
  rule?: boolean;
  /** the title lane; off when the plate's own picture already carries the title */
  head?: boolean;
  cls?: string;
  children: ComponentChildren;
}) {
  const slice = (file: string) => `url(${art(`/art/howto-cut/slice/${file}.png`)})`;
  const frame = plate === 'panel9' ? art('/art/popup/slice/panel9.png') : plate === 'addfriends' ? art('/art/popup/slice/addfriends.bg.png') : plate === 'crew' ? art('/art/popup/slice/crew.bg.png') : '';
  return (
    <div class={`howto-panel${plate === 'howto' ? '' : ` pp-${plate}`}${place ? '' : ' pp-mid'}${cls ? ` ${cls}` : ''}`} role="dialog" aria-label={label ?? title} data-testid={testid}
      style={{ ...(place ? { left: place.x, top: place.y } : null), width: place?.w ?? POPUP.w, '--frame': frame ? `url(${frame})` : slice('panel-9slice'), '--slice': SLICE }}
      onClick={(e) => e.stopPropagation()}>
      {/* the corner palms are fixed 246 × 222 pieces that must NOT stretch with the frame (mockup §"Kehys ja viipalointi") */}
      {palms && (['tl', 'tr', 'bl', 'br'] as const).map((c) => <img key={c} class={`ht-palm ht-palm-${c}`} src={art(`/art/howto-cut/slice/palm-${c}.png`)} alt="" draggable={false} />)}
      <button class="ht-close" type="button" data-testid={closeTestid} aria-label="Close" onClick={onClose}>
        <img src={art('/art/ui4b/btn-close-x@2x.png')} alt="" draggable={false} />
      </button>
      {titleArt && <img class="pp-title" src={art(titleArt)} alt={title} draggable={false} />}
      {head && !titleArt && <div class="ht-head"><span class="ht-title">{title}</span></div>}
      {rule && !titleArt && <img class="ht-rule" src={art('/art/howto-cut/slice/title-rule.png')} alt="" draggable={false} />}
      {children}
    </div>
  );
}
